import { useState } from "react";
import { Box } from "@mui/material";
import { DotLegalCheckbox, DotLegalDatePicker, DotLegalDialog, DotLegalSelect } from "@dotlegal/dotlegal-ui-components";
import { useTranslation } from "@localization/useTranslation";

interface ScheduleMitigationDialogProps {
    initialResponsibleId: string | null;
    initialDeadline: string | null;
    responsibleOptions: Array<{ id: string; name: string }>;
    isLoadingResponsibles: boolean;
    inProgress: boolean;
    canTrackAsTask: boolean;
    onClose: () => void;
    onSubmit: (responsibleId: string, deadline: Date, trackAsTask: boolean) => void;
}

// Approved -> planned requires a responsible and a deadline (optional while proposed). Cancelling
// leaves the plan in its previous status because the status change only fires on confirm.
function ScheduleMitigationDialog({
    initialResponsibleId,
    initialDeadline,
    responsibleOptions,
    isLoadingResponsibles,
    inProgress,
    canTrackAsTask,
    onClose,
    onSubmit,
}: ScheduleMitigationDialogProps) {
    const { translateString, getDateTimePickerLanguage } = useTranslation();
    const [responsibleId, setResponsibleId] = useState<string | null>(initialResponsibleId);
    const [deadline, setDeadline] = useState<Date | null>(initialDeadline ? new Date(initialDeadline) : null);
    const [trackAsTask, setTrackAsTask] = useState(canTrackAsTask);

    const canSubmit = responsibleId !== null && deadline !== null;

    return (
        <DotLegalDialog
            open
            header={translateString("riskMitigationPlanDialogTitle")}
            buttonOkText={translateString("save")}
            okButtonDisabled={!canSubmit}
            inProgress={inProgress}
            onDialogClose={onClose}
            onOkClick={() => {
                if (responsibleId !== null && deadline !== null) onSubmit(responsibleId, deadline, trackAsTask);
            }}
            size={"sm"}
        >
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <DotLegalSelect
                    label={translateString("responsible")}
                    options={responsibleOptions}
                    selectedItem={responsibleId}
                    onChange={setResponsibleId}
                    isLoading={isLoadingResponsibles}
                    noOptionsLabel={translateString("noData")}
                    noMargin
                />
                <DotLegalDatePicker
                    label={translateString("deadline")}
                    value={deadline}
                    onChange={setDeadline}
                    language={getDateTimePickerLanguage()}
                    noMargin
                />
                {canTrackAsTask && <DotLegalCheckbox label={translateString("riskMitigationTrackAsTask")} checked={trackAsTask} onChange={setTrackAsTask} />}
            </Box>
        </DotLegalDialog>
    );
}

export default ScheduleMitigationDialog;
